import { EventInput } from "@fullcalendar/core/structs/event";
import { EventSourceInput } from "@fullcalendar/core/structs/event-source";
import { GoogleService, waitForGoogleLibraryLoadingToFinish } from "./google-service";
import { EventEmitter } from "@/utils/event-emitter";
import { Axios } from "@/utils/axios";
import { ServerConfig } from "@/config/config";
import { IInitAppResponse } from "./init-service";
import { HttpError } from "@/utils/services/http-error";

export class GoogleCalendarServiceClass extends EventEmitter{
	public readonly url = `${ServerConfig.host}/google-calendar`
	private client:null | google.accounts.oauth2.CodeClient = null;
	private _isLoggedIn:boolean = false;
	private _loggedInUserEmail:string = "";
	public getLoggedInUserEmail():string {
		return this._loggedInUserEmail;
	}
	public isLoggedIn():boolean {
		return this._isLoggedIn;
	}


	async getCalendars(): Promise<gapi.client.calendar.CalendarListEntry[]> {
		if (!this.isLoggedIn) return [];
		let res = await Axios.get(`${this.url}/calendar-list`);
		return res.data.items;
	}

	async getCalendar(calendarId:string):Promise<gapi.client.calendar.CalendarListEntry>{
		let res = await Axios.get(`${this.url}/calendar/${calendarId}`);
		return res.data;
	}

	async getCalendarEvents( calendarId: string, beginTime: Date, endTime: Date, timeZone?: string): Promise<gapi.client.calendar.Event[]> {
		let res = await Axios.get(`${this.url}/calendar/${calendarId}/events`, {
			params: {
				timeMin: beginTime.toJSON(),
				timeMax: endTime.toJSON(),
				singleEvents: true,
				orderBy: "startTime",
				timeZone
			}
		});
		return res.data.items;
	}


	createNewCalendarListObject(): gapi.client.calendar.CalendarListEntry {
		return {
			description: ""
		} as gapi.client.calendar.CalendarListEntry;
	}

	createNewCalendarEventObject(): gapi.client.calendar.Event {
		return {
			id: "",
			start: {
				//dateTime: ""
			},
			end: {
				//dateTime: ""
			},
			summary: ""
		} as gapi.client.calendar.Event;
	}

	async createCalendar(summary: string, backgroundColor: string): Promise<gapi.client.calendar.CalendarListEntry> {
		let result = await Axios.post(`${this.url}/calendar`, {
			Post: {
				summary
			},
			Update: {
				backgroundColor: backgroundColor.toLocaleLowerCase(),
				foregroundColor: "#ffffff"
			}
		});
		return this.getCalendar(result.data.id);
	}

	async updateCalendar(calendarId: string, summary: string, backgroundColor: string): Promise<gapi.client.calendar.CalendarListEntry> {
		await Axios.patch(`${this.url}/calendar/${calendarId}`, {
			Post: {
				summary
			},
			Update: {
				backgroundColor: backgroundColor.toLocaleLowerCase(),
			}
		});
		return this.getCalendar(calendarId);
	}

	async deleteCalendar(calendarId: string) {
		await Axios.delete(`${this.url}/calendar/${calendarId}`);
	}

	async getEvent(eventId: string, calendarId: string): Promise<gapi.client.calendar.Event> {
		let result = await Axios.get(`${this.url}/calendar/${calendarId}/event/${eventId}`);
		return result.data;
	}

	async postEvent(event: gapi.client.calendar.Event, calendarId: string): Promise<gapi.client.calendar.Event> {
		let result = await Axios.post(`${this.url}/calendar/${calendarId}/event`, event);
		return result.data;
	}

	async putEvent(event: gapi.client.calendar.Event, calendarId: string): Promise<gapi.client.calendar.Event> {
		let result = await Axios.put(`${this.url}/calendar/${calendarId}/event/${event.id}`, event);
		return result.data;
	}

	async deleteEvent(event: gapi.client.calendar.Event, calendarId: string): Promise<void> {
		await Axios.delete(`${this.url}/calendar/${calendarId}/event/${event.id}`);
	}

	gCalToFullCalEvents(events: gapi.client.calendar.Event[], calendar: gapi.client.calendar.CalendarListEntry): EventInput[] {
		return events.map(item => {
			return {
				id: item.id,
				title: item.summary,
				start: item.start.dateTime || item.start.date, // try timed. will fall back to all-day
				end: item.end.dateTime || item.end.date, // same
				//url: item.htmlLink,
				location: item.location,
				description: item.description,
				backgroundColor: calendar.backgroundColor,
				extendedProps: {
					id: item.id,
					isGoogle: true,
					calendarId: calendar.id,
					location: item.location || "",
					description: item.description || ""
				}
			} as EventInput;
		});
	}

	createFullCalendarEventSource(calendar: gapi.client.calendar.CalendarListEntry): EventSourceInput {
		return {
			async events(info, onSuccess, onFail): Promise<EventInput[]> {
				let events = await GoogleCalendarService.getCalendarEvents(calendar.id, info.start, info.end, info.timeZone);
				try{
					return GoogleCalendarService.gCalToFullCalEvents(events, calendar);
				}catch(err){
					console.log(err);
					throw err;
				}
			}
		} as EventSourceInput;
	}

	async login():Promise<void>{
		if (!this.client) return;
		let ref = this;
		this.client.requestCode();
		let code = await new Promise<string>((resolve, reject)=>{
			ref.once("login-result", (code:string, err:Error)=>{
				if (err){
					reject(err);
					return;
				}
				resolve(code);
			});
		});
		await Axios.post(`${this.url}/login`, {Code: code, RedirectURL: window.location.origin});
		await this.checkIsLoggedIn();
		await this.getAccountInfo();
	}

	async logout():Promise<void>{
		await Axios.post(`${this.url}/logout`);
		await this.checkIsLoggedIn();
	}

	async checkIsLoggedIn():Promise<boolean>{
		let result = await Axios.get(`${this.url}/is-logged-in`);
		this._isLoggedIn = result.data.LoggedIn == true;
		if (!this.isLoggedIn) {
			this._loggedInUserEmail = "";
		}
		return this._isLoggedIn;
	}

	public async getAccountInfo():Promise<string>{
		if (!this.isLoggedIn) return "";
		if (this._loggedInUserEmail) return this._loggedInUserEmail;
		try{
			let result = await Axios.get(`${this.url}/account`);
			this._loggedInUserEmail = result.data.email;
			return this._loggedInUserEmail;
		}catch(err){
			let hErr = err as HttpError;
			if (hErr.status == 401) {
				hErr.dontShow = true;
			}
			throw err;
		}
	}

	public async init(data:IInitAppResponse){
		this._isLoggedIn = data.GoogleCalendarIsLoggedIn;
	}

	public async initClient(){
		await waitForGoogleLibraryLoadingToFinish();
		let ref = this;
		this.client = google.accounts.oauth2.initCodeClient({
			client_id: GoogleService.CLIENT_ID,
			scope: [
				"email",
				"profile",
				"https://www.googleapis.com/auth/calendar",
			].join(" "),
			ux_mode: "popup",
			callback(response:google.accounts.oauth2.CodeResponse){
				ref.emit("login-result", response.code, null);
				ref.emit("auth");
			},
			error_callback(error:google.accounts.oauth2.ClientConfigError){
				ref.emit("login-result", null, new Error(error.message));
			}
		});
	}
};

export const GoogleCalendarService = new GoogleCalendarServiceClass();
